import React, { useState, useMemo, useEffect } from "react";
import TextField, { TextFieldProps } from "@mui/material/TextField";
import { debounce } from "lodash";

type DebouncedTextFieldProps = Omit<TextFieldProps, 'onChange'> & {
  value: string;
  onDebouncedChange: (value: string) => void;
  debounceDelay?: number;
};

export default function DebouncedTextField({
  value,
  onDebouncedChange,
  debounceDelay: debounceDelayProp,
  ...props
}: DebouncedTextFieldProps) {
  const debounceDelay = debounceDelayProp ?? 300;

  const [localEdit, setLocalEdit] = useState<{ sourceValue: string; value: string }>();
  const localValue = localEdit?.sourceValue === value ? localEdit.value : value;

  /* The callback travels with the value rather than being closed over, so that the debounced
     function depends on nothing that changes per render and keeps its identity. Depending on
     onDebouncedChange would rebuild it — and the cleanup below would cancel the pending push —
     on every render of a parent that passes an inline arrow, which is every caller whose
     component the React Compiler cannot memoize. That loss is invisible: localEdit goes on
     showing the typed text, so it reads as a form ignoring a field it plainly displays.
     Correctness here must not depend on a caller memoizing, nor on the compiler doing it for
     them. lodash invokes with the arguments of the most recent call, so the callback that runs
     is the one the field was rendered with when the user last typed — the same one an
     undelayed onChange would have reached. */
  const pushToParent = useMemo(() =>
    debounce((newVal: string, notifyParent: (newValue: string) => void) => {
      notifyParent(newVal);
    }, debounceDelay),
    [debounceDelay]
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalEdit({ sourceValue: value, value: e.target.value }); // Instant local update for fast typing
    pushToParent(e.target.value, onDebouncedChange);  // Delayed parent update
  };

  useEffect(() => {
    return () => pushToParent.cancel();
  }, [pushToParent]);

  return <TextField {...props} value={localValue} onChange={handleChange} />;
}
